import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useClientProject } from '@/contexts'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { SemanticDashboard } from '@/components/charts/SemanticDashboard'
import { DashboardGridSkeleton } from '@/components/route-shell/skeletons/DashboardGridSkeleton'
import { getCustomDashboard } from '@/services/dashboardService'
import { chatbotService } from '@/services'
import { ChatInput, ChatMessage as ChatMessageBubble } from '@/components/ChatBot'
import type { ChatMessage, ConversationMessage } from '@/types'
import { isDashboardDefinition } from '@chart-core'
import type { DashboardInput } from '@chart-core'
import '@/components/ChatBot/ChatBot.css'
import '@/styles/pages.css'
import '@/styles/custom-dashboards.css'

interface DashboardBuilderSearch {
  /** The dashboard this builder session is editing; absent = blank, adopted on the first save. */
  dashboard?: string
  /** active section tab within the previewed dashboard */
  tab?: string
}

// The prompt-driven dashboard builder: a chat panel (the dashboard-builder agent behind
// GET /api/chatbot/dashboard) beside a live preview of the ONE dashboard the conversation
// builds. The agent streams a DASHBOARD part every time it saves, which is what flips this
// page from the blank state to the saved dashboard — and refreshes it on every later save.
export const Route = createFileRoute('/_app/organization/custom-dashboards_/new')({
  head: () => ({ meta: [{ title: 'Dashboard Builder · AERRIS' }] }),
  component: DashboardBuilderPage,
  validateSearch: (search: Record<string, unknown>): DashboardBuilderSearch => ({
    dashboard: typeof search.dashboard === 'string' ? search.dashboard : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
})

/** The definition is schema-validated on write, but guard the render against a junk row anyway. */
const isRenderable = (definition: unknown): definition is DashboardInput =>
  Array.isArray(definition) || isDashboardDefinition(definition as DashboardInput)

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  color: 'var(--text-muted, #6b7280)',
  textAlign: 'center',
  backgroundColor: 'var(--container-bg, #ffffff)',
  border: '1px solid var(--border-color, #e5e7eb)',
  borderRadius: 'var(--radius-lg)',
  padding: '2rem',
}

function Panel({ title, body }: { title: string; body?: string }) {
  return (
    <div style={panelStyle}>
      <h2 style={{ fontSize: '1.125rem', margin: 0 }}>{title}</h2>
      {body && <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>{body}</p>}
    </div>
  )
}

const chatPanelStyle: CSSProperties = {
  width: '380px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  borderRight: '1px solid var(--border-color, #e5e7eb)',
  backgroundColor: 'var(--container-bg, #ffffff)',
}

function DashboardBuilderPage() {
  const { projectId } = useClientProject()
  const { dashboard: dashboardParam, tab } = Route.useSearch()
  // The param arrives in either id form (short from new links, dashed from old ones); the API
  // calls and the agent protocol below speak UUIDs.
  const dashboardUuid = normalizeEntityId(dashboardParam)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [conversation, setConversation] = useState<ConversationMessage[]>([])
  const [lastThought, setLastThought] = useState<string | null>(null)
  const [awaitingResponse, setAwaitingResponse] = useState(false)
  const [chatSessionUuid] = useState(() => crypto.randomUUID())

  const messagesRef = useRef<HTMLDivElement>(null)
  const closeConnectionRef = useRef<(() => void) | null>(null)
  // The dashboard the NEXT message must name. A ref rather than the search param alone, because
  // the agent saves mid-stream: the DASHBOARD part must take effect immediately, not after the
  // URL navigation settles.
  const dashboardUuidRef = useRef<string | undefined>(dashboardUuid)
  useEffect(() => {
    dashboardUuidRef.current = dashboardUuid
  }, [dashboardUuid])

  useEffect(() => {
    messagesRef.current?.lastElementChild?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [conversation, lastThought])

  useEffect(() => {
    return () => closeConnectionRef.current?.()
  }, [])

  const selected = useQuery({
    queryKey: ['custom-dashboard', projectId, dashboardUuid],
    queryFn: () => getCustomDashboard(projectId!, dashboardUuid!),
    enabled: !!projectId && !!dashboardUuid,
  })

  const handleMessageReceived = (message: ChatMessage) => {
    if (message.type === 'THINKING') {
      setLastThought(message.message ?? null)
    } else if (message.type === 'DASHBOARD' && message.dashboard) {
      const { uuid } = message.dashboard
      dashboardUuidRef.current = uuid
      // The saved definition changed server-side; drop cached copies so the preview re-renders.
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard', projectId, uuid] })
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards', projectId] })
      navigate({
        to: '/organization/custom-dashboards/new',
        search: (prev) => ({ ...prev, dashboard: toShortEntityId(uuid) }),
        replace: true,
      })
    } else if (message.type === 'ANSWER') {
      setAwaitingResponse(false)
      setLastThought(null)
      setConversation((prev) => [
        ...prev,
        message.answer
          ? { answer: message.answer, sender: 'ASSISTANT' }
          : { message: message.message ?? '', sender: 'ASSISTANT' },
      ])
    } else if (message.type === 'QUESTION' && message.question) {
      // The agent asked a clarifying question and ended its turn (the stream is closed). The
      // question joins the transcript as a QUESTION block — its chips send the answer via
      // handleSend — and re-enabling the input makes free text the other answer path.
      const question = message.question
      setAwaitingResponse(false)
      setLastThought(null)
      setConversation((prev) => [
        ...prev,
        { sender: 'ASSISTANT', answer: { blocks: [{ type: 'QUESTION', question }] } },
      ])
    } else if (message.type === 'ERROR') {
      setAwaitingResponse(false)
      setLastThought(null)
      setConversation((prev) => [...prev, { message: `Error: ${message.message}`, sender: 'ASSISTANT' }])
    }
  }

  const handleSend = (message: string) => {
    if (!projectId) return

    setAwaitingResponse(true)
    setLastThought('Planning')
    setConversation((prev) => [...prev, { message, sender: 'USER' }])

    closeConnectionRef.current?.()
    closeConnectionRef.current = chatbotService.sendDashboardMessage(
      {
        message,
        chatSessionUuid,
        projectUuid: projectId,
        dashboardUuid: dashboardUuidRef.current,
      },
      handleMessageReceived,
    )
  }

  const backToDashboards = () =>
    navigate({
      to: '/organization/custom-dashboards',
      search: (prev) => ({
        ...prev,
        dashboard: dashboardUuid ? toShortEntityId(dashboardUuid) : undefined,
        tab: undefined,
      }),
    })
  const setTab = (next: string) =>
    navigate({
      to: '/organization/custom-dashboards/new',
      search: (prev) => ({ ...prev, tab: next }),
    })

  if (!projectId) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Panel title="Select a Client and Project" body="Choose a client and project to build a dashboard for" />
      </div>
    )
  }

  return (
    <div className="custom-dash-page">
      <aside style={chatPanelStyle}>
        <div className="chatbot-header" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <button
            type="button"
            onClick={backToDashboards}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted, #6b7280)',
              padding: 0,
              textAlign: 'left',
              fontSize: '0.8125rem',
            }}
          >
            ← All dashboards
          </button>
          <h3>Dashboard builder</h3>
        </div>
        <div className="chatbot-messages" ref={messagesRef}>
          {conversation.length === 0 && (
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted, #6b7280)' }}>
              {dashboardUuid
                ? 'Describe the changes you want — the assistant updates this dashboard as you go.'
                : 'Describe the dashboard you want — e.g. "Employees whose contracts have return-of-equipment clauses, with a headline count". It appears beside this chat as it is built.'}
            </p>
          )}
          {conversation.map((msg, idx) => (
            <ChatMessageBubble key={idx} message={msg} onQuestionOption={handleSend} />
          ))}
          {awaitingResponse && lastThought && <div className="chat-thinking">{lastThought}...</div>}
        </div>
        <div className="chatbot-footer">
          <ChatInput onSend={handleSend} disabled={awaitingResponse} placeholder="Describe your dashboard..." />
        </div>
      </aside>
      <main style={{ flex: 1, overflow: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {!dashboardUuid ? (
          <Panel
            title="A blank dashboard"
            body="Nothing here yet — describe what you want to see in the chat and the dashboard will appear as it is built."
          />
        ) : selected.isPending ? (
          <DashboardGridSkeleton label="dashboard" />
        ) : selected.isError ? (
          <Panel title="Couldn't load this dashboard" body={String(selected.error)} />
        ) : !selected.data ? (
          <Panel title="Dashboard not found" body="It may have been deleted, or it belongs to another project." />
        ) : !isRenderable(selected.data.definition) ? (
          <Panel title="This dashboard can't be rendered" body="Its saved definition isn't a recognised dashboard shape." />
        ) : (
          <>
            <header style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
              <h1 style={{ fontSize: '1.25rem', margin: 0 }}>{selected.data.name ?? 'Untitled dashboard'}</h1>
            </header>
            <SemanticDashboard
              projectUuid={projectId}
              definitions={selected.data.definition}
              tab={tab}
              onTabChange={setTab}
            />
          </>
        )}
      </main>
    </div>
  )
}
